/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-dataset
 * @responsibility Provide the pure lookups and derivations the workspace screens use (`act`, `run`, `myRuns`, `score`…).
 * @state-owner none
 * @allowed-imports shared view types, demo constants, the translator type and the status glyph type
 * @forbidden React; network; store implementation
 * @public-api workspace shell and modules
 * @side-effects none
 * @tests workspace-behaviour.test.ts
 */

import type { StatusGlyph } from '../components/StatusIcon';
import { DEFAULT_STUCK_MINUTES, ME, QUESTIONS, SEAT_LABEL, STUDENT_FIX_RUN_IDS } from './demo-data';
import type { Translator } from './i18n';
import type {
  ActivityRecord,
  AnswerMark,
  ClassRecord,
  RunRecord,
  SeatRecord,
  SeatStatus,
  WorkspaceRole,
  WorkspaceSection,
} from './model';

export function findActivity(
  activities: readonly ActivityRecord[],
  id: string | null,
): ActivityRecord | undefined {
  return id ? activities.find((item) => item.id === id) : undefined;
}

export function findRun(runs: readonly RunRecord[], id: string | null): RunRecord | undefined {
  return id ? runs.find((item) => item.id === id) : undefined;
}

export function runName(activities: readonly ActivityRecord[], run: RunRecord): string {
  return findActivity(activities, run.activityId)?.name ?? '';
}

export function currentClassIndex(selection: string | null): number {
  const match = /^c(\d)$/.exec(selection ?? '');
  return match ? Number(match[1]) : 0;
}

export function classRuns(runs: readonly RunRecord[], classIndex: number): readonly RunRecord[] {
  return runs.filter((item) => item.classIndex === classIndex);
}

export function classMaterials(
  activities: readonly ActivityRecord[],
  classIndex: number,
): readonly ActivityRecord[] {
  return activities.filter((item) => item.usedByClassIndexes.includes(classIndex));
}

export function liveRuns(runs: readonly RunRecord[]): readonly RunRecord[] {
  return runs.filter((item) => item.status === 'live');
}

export function myClassIndexes(
  classes: readonly ClassRecord[],
  me: string = ME,
): readonly number[] {
  return classes.map((_, index) => index).filter((index) => classes[index].students.includes(me));
}

export function isRunForMe(
  classes: readonly ClassRecord[],
  run: RunRecord,
  me: string = ME,
): boolean {
  const classroom = classes[run.classIndex];
  if (!classroom?.students.includes(me)) return false;
  return run.audience !== 'some' || (run.students ?? []).includes(me);
}

export function myRuns(
  classes: readonly ClassRecord[],
  runs: readonly RunRecord[],
  me: string = ME,
): readonly RunRecord[] {
  return runs.filter((run) => isRunForMe(classes, run, me));
}

export type StudentRunState = 'live' | 'open' | 'fix' | 'done';

export function studentRunState(run: RunRecord): StudentRunState {
  if (run.status === 'live') return 'live';
  if (run.status === 'open') return 'open';
  return STUDENT_FIX_RUN_IDS.includes(run.id) ? 'fix' : 'done';
}

export function findSeat(seats: readonly SeatRecord[], student: string): SeatRecord | undefined {
  return seats.find((item) => item.name === student);
}

export function seatQuestion(seat: SeatRecord, total: number): number {
  return Math.max(1, Math.min(total, Math.round((seat.progress / 20) * total)));
}

export function defaultSelection(
  section: WorkspaceSection,
  role: WorkspaceRole,
  classes: readonly ClassRecord[],
  runs: readonly RunRecord[],
): string | null {
  if (section === 'room') {
    return role === 'teacher' ? 'c0' : `c${myClassIndexes(classes)[0] ?? 0}`;
  }
  if (section === 'live') {
    const run = liveRuns(runs)[0];
    return run ? run.id : null;
  }
  if (section === 'rep') return role === 'teacher' ? (classes[0]?.students[0] ?? null) : null;
  return null;
}

/** The per-cell state the response matrix renders. */
export function cellState(
  name: string,
  questionIndex: number,
): 'ok' | 'self' | 'bad' | 'iv' | 'none' {
  if (name === '王柏睿' && questionIndex === 2) return 'iv';
  if (name === '陳思妤' && questionIndex === 2) return 'self';
  const hash = (name.charCodeAt(0) * 7 + name.charCodeAt(1) * 3 + questionIndex * 13) % 10;
  if (hash <= 4) return 'ok';
  if (hash === 5) return 'self';
  if (hash <= 7) return 'bad';
  if (hash === 8) return 'none';
  return 'ok';
}

export interface AnswerScore {
  readonly correct: number;
  readonly fixed: number;
  readonly unjudged: number;
  readonly total: number;
  readonly percent: number;
}

export function scoreAnswers(marks: readonly AnswerMark[]): AnswerScore {
  const correct = marks.filter((mark) => mark === 'ok' || mark === 'fix').length;
  return {
    correct,
    fixed: marks.filter((mark) => mark === 'fix').length,
    unjudged: marks.filter((mark) => mark === 'low').length,
    total: marks.length,
    percent: Math.round((correct / marks.length) * 100),
  };
}

/**
 * A sheet only needs the teacher's own eyes while some answer is still unjudged. Everything else
 * has already been decided by the marking, so its comment can be generated without them opening it.
 */
export function awaitsTeacher(marks: readonly AnswerMark[]): boolean {
  return marks.some((mark) => mark === 'low');
}

export interface SeatPresentation {
  readonly glyph: StatusGlyph;
  readonly tone: 'hot' | 'wn' | '';
  /** True while the student is blocked and the teacher should step in. */
  readonly needsTeacher: boolean;
}

const SEAT_PRESENTATION: Readonly<Record<SeatStatus, SeatPresentation>> = {
  offline: { glyph: 'todo', tone: '', needsTeacher: false },
  online: { glyph: 'done', tone: '', needsTeacher: false },
  ok: { glyph: 'live', tone: '', needsTeacher: false },
  stuck: { glyph: 'warn', tone: 'wn', needsTeacher: true },
  need: { glyph: 'err', tone: 'hot', needsTeacher: true },
};

/** One mapping from seat status to glyph and tone, so every screen reads a status the same way. */
export function seatPresentation(status: SeatStatus): SeatPresentation {
  return SEAT_PRESENTATION[status];
}

/** How loudly a status asks for the teacher. The live board orders its groups by this. */
const SEAT_URGENCY: Readonly<Record<SeatStatus, number>> = {
  need: 4,
  stuck: 3,
  ok: 2,
  online: 1,
  offline: 0,
};

export function seatUrgency(status: SeatStatus): number {
  return SEAT_URGENCY[status];
}

/** `stuck` is the only status whose label carries a value, so labels are formatted here. */
export function seatStatusLabel(seat: SeatRecord, t: Translator): string {
  if (seat.status === 'stuck') {
    return `${t('卡住')} ${seat.stuckMinutes ?? DEFAULT_STUCK_MINUTES} ${t('分鐘')}`;
  }
  return t(SEAT_LABEL[seat.status]);
}

export interface MarkTally {
  readonly ok: number;
  readonly fix: number;
  readonly low: number;
  readonly no: number;
}

/** Counts a marked sheet by judgement, so a row can say how many were right, fixed or missed. */
export function markTally(marks: readonly AnswerMark[]): MarkTally {
  return {
    ok: marks.filter((mark) => mark === 'ok').length,
    fix: marks.filter((mark) => mark === 'fix').length,
    low: marks.filter((mark) => mark === 'low').length,
    no: marks.filter((mark) => mark === 'no').length,
  };
}

export function guardianCode(student: string): string {
  let hash = 0;
  for (const character of student) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return `P${String(hash % 100000000).padStart(8, '0')}`;
}

export function guardianLink(student: string): string {
  return `https://vispo.app/g/${guardianCode(student).toLowerCase()}`;
}

export function fuzzyMatch(query: string, text: string): boolean {
  if (!query) return true;
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  let index = 0;
  for (const character of needle) {
    index = haystack.indexOf(character, index);
    if (index < 0) return false;
    index += 1;
  }
  return true;
}

export const QUESTION_COUNT = QUESTIONS.length;

export function currentTimeLabel(now: Date = new Date()): string {
  return now.toTimeString().slice(0, 5);
}
